const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const toUint8Array = (value: ArrayBuffer | Uint8Array) => {
  return value instanceof Uint8Array ? value : new Uint8Array(value);
};

const toBase64Url = (buffer: ArrayBuffer | Uint8Array) => {
  const bytes = toUint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (value: string) => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(value.length + (4 - (value.length % 4)) % 4, '=');
  const binary = atob(padded);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer;
};

export const encryptPassword = async (password: string) => {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt']
  );

  const rawKey = await crypto.subtle.exportKey('raw', key);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = textEncoder.encode(password);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);

  return {
    ciphertext: toBase64Url(encrypted),
    iv: toBase64Url(iv),
    key: toBase64Url(rawKey)
  };
};

export const decryptPassword = async (ciphertext: string, iv: string, key: string) => {
  const keyData = fromBase64Url(key);
  const cryptoKey = await crypto.subtle.importKey('raw', keyData, 'AES-GCM', false, ['decrypt']);
  const ivData = fromBase64Url(iv);
  const cipherData = fromBase64Url(ciphertext);

  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivData }, cryptoKey, cipherData);
  return textDecoder.decode(decrypted);
};

export const encodeMetadata = (metadata: Record<string, unknown>) => {
  const json = JSON.stringify(metadata);
  return toBase64Url(textEncoder.encode(json));
};

export const decodeMetadata = (encoded?: string | null): Record<string, unknown> | null => {
  if (!encoded) {
    return null;
  }
  try {
    const buffer = fromBase64Url(encoded);
    return JSON.parse(textDecoder.decode(buffer));
  } catch (error) {
    return null;
  }
};
